import { useCallback, useEffect, useMemo, useState } from 'react'
import FileSaver from 'file-saver'
import { AttachmentPromiseClient } from '@trustero/trustero-api-web/lib/attachment/attachment_grpc_web_pb'
import {
  Document as EvidenceDocument,
  Document,
  DOCUMENT_TYPE,
  GetDocumentByOIDRequest,
  GetDocumentsByOIDsRequest,
  GetExclusionsResponse,
  GetExclusionsRequest,
  AddDocumentRequest,
} from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import log from 'loglevel'
import { HTTP_METHODS } from 'src/Utils/globalEnums'
import {
  ContentID,
  URL as ProtoURL,
} from '@trustero/trustero-api-web/lib/contentstore/contentstore_pb'
import {
  BoolValue,
  StringValue,
} from 'google-protobuf/google/protobuf/wrappers_pb'
import { ToastPrompts } from 'src/Utils/helpers/toast'
import { ParsedPart } from 'src/Utils/globalTypes'
import { ContentStorePromiseClient } from '@trustero/trustero-api-web/lib/contentstore/contentstore_grpc_web_pb'
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid'
import { useInAudit } from 'src/context/AuditContext'
import { isGrpcError } from 'src/Utils/isGrpcError'
import {
  getIsMultiPartMixedMime,
  parseMultiPartResponse,
} from 'src/Utils/globalHelpers'
import {
  getEvidenceCsv,
  getExcelColumns,
  getExcelRows,
  getExcelSheet,
} from 'src/Utils/Evidence/evidence.helpers'
import { ColumnHeaderTooltip } from 'src/Utils/Evidence/evidence.types'
import { convertDocToObject, DocumentObj } from 'src/adapter/AttachmentAdapter'
import { useAuthorizedGrpcClient } from '../../../adapter'
import { useSwrImmutableGrpc } from '../useSwrImmutableGrpc'
import { GrpcResponse, NewGrpcResponse } from '../hooks/types'
import { useSwrGrpc } from '../useSwrGrpc'

// Hook for fetching a single document by ID.
export const useDocument = ({
  documentId,
  includeBody = false,
  shouldFetch = true,
}: {
  documentId: string
  includeBody?: boolean
  shouldFetch?: boolean
}): GrpcResponse<Document> => {
  const { auditId } = useInAudit()

  const request = new GetDocumentByOIDRequest()
    .setId(documentId)
    .setIncludeBody(includeBody)

  if (auditId) {
    request.setAuditId(new StringValue().setValue(auditId))
  }

  const { response } = useSwrGrpc(
    AttachmentPromiseClient.prototype.getDocumentByOID,
    request,
    shouldFetch || !!documentId,
  )
  return NewGrpcResponse(response)
}

export const useDocumentsByIds = ({
  documentIds,
  shouldFetch = true,
}: {
  documentIds: string[]
  shouldFetch?: boolean
}): {
  documents: DocumentObj[]
  isLoading: boolean
  error: Error | null
} => {
  const client = useAuthorizedGrpcClient(AttachmentPromiseClient)
  const { auditId } = useInAudit()
  const normalizedDocumentIdsKey = useMemo(
    () =>
      Array.from(
        new Set(
          documentIds.map((documentId) => documentId.trim()).filter(Boolean),
        ),
      ).join(','),
    [documentIds],
  )
  const normalizedDocumentIds = useMemo(
    () =>
      normalizedDocumentIdsKey
        ? normalizedDocumentIdsKey.split(',').filter(Boolean)
        : [],
    [normalizedDocumentIdsKey],
  )
  const [documents, setDocuments] = useState<DocumentObj[]>([])
  const [isLoading, setIsLoading] = useState(
    shouldFetch && normalizedDocumentIds.length > 0,
  )
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let cancelled = false

    if (!shouldFetch || normalizedDocumentIds.length === 0) {
      setDocuments([])
      setIsLoading(false)
      setError(null)
      return () => {
        cancelled = true
      }
    }

    setIsLoading(true)
    setError(null)

    const batchRequest = new GetDocumentsByOIDsRequest()
      .setIdsList(normalizedDocumentIds)
      .setIncludeBody(false)

    if (auditId) {
      batchRequest.setAuditId(new StringValue().setValue(auditId))
    }

    void client
      .getDocumentsByOIDs(batchRequest)
      .then((response) => {
        if (cancelled) {
          return
        }
        setDocuments(
          response
            .getDocumentsList()
            .map((document) => convertDocToObject(document)),
        )
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return
        }
        setDocuments([])
        setError(
          err instanceof Error ? err : new Error('Failed to load documents'),
        )
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [
    auditId,
    client,
    normalizedDocumentIdsKey,
    normalizedDocumentIds,
    shouldFetch,
  ])

  return { documents, isLoading, error }
}

export const useAssociateDLREvidence = (): ((
  requestId: string,
  documentId: string,
  fetchDocumentBody: () => Promise<Uint8Array | string | undefined>,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(AttachmentPromiseClient)
  const toEvidence = (requestId: string, document: Document) => {
    return new EvidenceDocument()
      .setSubjectmodelid('')
      .setDoctype(DOCUMENT_TYPE.EVIDENCE)
      .setMime(document.getMime())
      .setBody(document.getBody())
      .setCaption(document.getCaption())
      .setActor(document.getActor())
      .setActortype(document.getActortype())
      .setRelevantdate(document.getRelevantdate())
      .setRequestid(requestId)
      .setSubjectmodeltype(MODEL_TYPE.CONTROL)
  }

  return async (
    requestId: string,
    documentId: string,
    fetchDocumentBody: () => Promise<Uint8Array | string | undefined>,
  ) => {
    try {
      const document = await client.getDocumentByOID(
        new GetDocumentByOIDRequest().setId(documentId),
      )
      const body = await fetchDocumentBody()
      document.setBody(body ?? '')
      await client.addDocument(
        new AddDocumentRequest()
          .setDocument(toEvidence(requestId, document))
          .setOverrideActor(new BoolValue().setValue(true))
          .setOverrideActorType(new BoolValue().setValue(true)),
      )
    } catch (error) {
      log.error('Error when adding evidence.', error)
    }
  }
}

export const useGetAttestationDownloadUrl = (
  contentId: string,
): (() => Promise<ProtoURL | undefined>) => {
  const { auditId } = useInAudit()
  const client = useAuthorizedGrpcClient(ContentStorePromiseClient)

  return useCallback(async () => {
    if (!contentId) {
      return undefined
    }
    try {
      const request = new ContentID()
        .setContentId(contentId)
        .setIsAttestation(new BoolValue().setValue(true))

      if (auditId) {
        request.setAuditId(new StringValue().setValue(auditId))
      }

      const res = await client.getDownloadURL(request)
      return res
    } catch (err) {
      log.error(
        `error getting content store download url, contentId: ${contentId} err:`,
        contentId,
        err,
      )
      return undefined
    }
  }, [auditId, client, contentId])
}

export const useGetDownloadUrls = (
  contentIds: string[],
): (() => Promise<(ProtoURL | undefined)[]>) => {
  const { auditId } = useInAudit()
  const client = useAuthorizedGrpcClient(ContentStorePromiseClient)

  return useCallback(async () => {
    const requests = contentIds.map(async (contentId) => {
      if (!contentId) {
        return undefined
      }
      try {
        const request = new ContentID().setContentId(contentId)

        if (auditId) {
          request.setAuditId(new StringValue().setValue(auditId))
        }

        const res = await client.getDownloadURL(request)
        return res
      } catch (err) {
        log.error(
          `error getting content store download url, contentId: ${contentId} err:`,
          contentId,
          err,
        )
        return undefined
      }
    })
    const res = await Promise.allSettled(requests)
    return res.map((r) => (r.status === 'fulfilled' ? r.value : undefined))
  }, [client, contentIds, auditId])
}

export const useGetDownloadUrl = (
  contentId: string,
): (() => Promise<ProtoURL | undefined>) => {
  const contentIds = useMemo(() => [contentId], [contentId])
  const getDownloadUrls = useGetDownloadUrls(contentIds)

  return useCallback(async () => {
    const urls = await getDownloadUrls()
    return urls[0]
  }, [getDownloadUrls])
}

export type FetchDocumentBodyInput = {
  contentId: string
  mime: string
  isAttestation?: boolean
}

export const useFetchAttestationUrlBody = (
  contentId: string,
  mime: string,
): ((
  url: ProtoURL | undefined,
) => Promise<Uint8Array | string | undefined>) => {
  return useCallback(
    async (url: ProtoURL | undefined) => {
      if (!url) {
        throw new Error(ToastPrompts.DOC_DOWNLOAD_ERROR)
      }
      const response = await fetch(url.getUrl(), {
        method: HTTP_METHODS.GET,
        headers: new Headers(
          url.getHeadersMap().getEntryList() as unknown as HeadersInit,
        ),
      })
      if (!response || response.status !== 200) {
        throw new Error(ToastPrompts.DOC_DOWNLOAD_ERROR)
      }
      let body: string | Uint8Array | ParsedPart[]
      if (mime.startsWith('text/') && mime !== 'text/csv') {
        body = await response.text()
      } else {
        const bodyBlob = await response.blob()
        const bodyBytes = new Uint8Array(
          await new Response(bodyBlob).arrayBuffer(),
        )
        body = bodyBytes
      }
      return body
    },
    [mime],
  )
}

export const useFetchUrlBodies = (
  inputs?: FetchDocumentBodyInput[],
): ((
  urls: (ProtoURL | undefined)[],
) => Promise<(Uint8Array | string | undefined)[]>) => {
  return useCallback(
    async (urls: (ProtoURL | undefined)[]) => {
      const bodies = await Promise.allSettled(
        urls.map(async (url, index) => {
          if (!url) {
            throw new Error(ToastPrompts.DOC_DOWNLOAD_ERROR)
          }
          const response = await fetch(url.getUrl(), {
            method: HTTP_METHODS.GET,
            headers: new Headers(
              url.getHeadersMap().getEntryList() as unknown as HeadersInit,
            ),
          })
          if (!response || response.status !== 200) {
            throw new Error(ToastPrompts.DOC_DOWNLOAD_ERROR)
          }
          let body: string | Uint8Array | ParsedPart[]
          if (
            inputs?.[index]?.mime.startsWith('text/') &&
            inputs?.[index]?.mime !== 'text/csv'
          ) {
            body = await response.text()
          } else {
            const bodyBlob = await response.blob()
            const bodyBytes = new Uint8Array(
              await new Response(bodyBlob).arrayBuffer(),
            )
            body = bodyBytes
          }
          return body
        }),
      )
      return bodies.map((b) => (b.status === 'fulfilled' ? b.value : undefined))
    },
    [inputs],
  )
}

export const useFetchDocumentBodies = (
  inputs: FetchDocumentBodyInput[],
): (() => Promise<(Uint8Array | string | undefined)[]>) => {
  const contentIds = useMemo(() => inputs.map((i) => i.contentId), [inputs])
  const getDownloadURLs = useGetDownloadUrls(contentIds)
  const getUrlBodies = useFetchUrlBodies(inputs)

  return useCallback(async () => {
    const urls = await getDownloadURLs()
    const bodies = await getUrlBodies(urls)
    return bodies
  }, [getDownloadURLs, getUrlBodies])
}

export const useFetchAttestationBody = (
  contentId: string,
  mime: string,
): (() => Promise<Uint8Array | string | undefined>) => {
  const getDownloadUrl = useGetAttestationDownloadUrl(contentId)
  const getAttestationBody = useFetchAttestationUrlBody(contentId, mime)

  return useCallback(async () => {
    const url = await getDownloadUrl()
    if (!url) {
      throw new Error(ToastPrompts.DOC_DOWNLOAD_ERROR)
    }
    const body = await getAttestationBody(url)
    if (!body) {
      throw new Error(ToastPrompts.DOC_DOWNLOAD_ERROR)
    }
    return body
  }, [getDownloadUrl, getAttestationBody])
}

export const useFetchDocumentBody = (
  contentId: string,
  mime: string,
): (() => Promise<Uint8Array | string | undefined>) => {
  const inputs = useMemo(() => [{ contentId, mime }], [contentId, mime])
  const getDocumentBodies = useFetchDocumentBodies(inputs)

  return useCallback(async () => {
    const bodies = await getDocumentBodies()
    if (!bodies[0]) {
      throw new Error(ToastPrompts.DOC_DOWNLOAD_ERROR)
    }
    return bodies[0]
  }, [getDocumentBodies])
}

class LruPromiseCache<K, V> extends Map<K, V> {
  private readonly maxEntries: number
  constructor(maxEntries: number) {
    super()
    this.maxEntries = maxEntries
  }
  override get(key: K): V | undefined {
    if (!super.has(key)) {
      return undefined
    }
    // Refresh key as most recently used by re-inserting it
    const value = super.get(key) as V
    super.delete(key)
    super.set(key, value)
    return value
  }
  override set(key: K, value: V): this {
    // If key already exists, delete first so insertion order is updated
    if (super.has(key)) {
      super.delete(key)
    }
    super.set(key, value)
    if (this.size > this.maxEntries) {
      const firstKey = this.keys().next().value as K | undefined
      if (firstKey !== undefined) {
        super.delete(firstKey)
      }
    }
    return this
  }
}

const documentBodyPromiseCache = new LruPromiseCache<
  string,
  Promise<Uint8Array | string | ParsedPart[] | undefined>
>(50)

export const useFetchDocumentBodyAllowMultipart = (
  contentId: string,
  mime: string,
): (() => Promise<Uint8Array | string | ParsedPart[] | undefined>) => {
  const getDownloadURL = useGetDownloadUrl(contentId)

  return useCallback(async () => {
    if (!contentId) {
      return undefined
    }
    const cacheKey = `${contentId}:${mime}`
    const cached = documentBodyPromiseCache.get(cacheKey)
    if (cached) {
      return cached
    }
    const fetchPromise = (async () => {
      const url = await getDownloadURL()
      if (!url) {
        throw new Error(ToastPrompts.DOC_DOWNLOAD_ERROR)
      }
      const headers = url.getHeadersMap().getEntryList()

      const response: Response = await fetch(url.getUrl(), {
        method: HTTP_METHODS.GET,
        headers: new Headers(headers as unknown as HeadersInit),
      })
      if (!response || response.status !== 200) {
        throw new Error(ToastPrompts.DOC_DOWNLOAD_ERROR)
      }
      let body: string | Uint8Array | ParsedPart[]
      if (getIsMultiPartMixedMime(mime)) {
        if (!response.body) {
          throw new Error(ToastPrompts.DOC_DOWNLOAD_ERROR)
        }
        const parts = await parseMultiPartResponse(response.body, mime)
        body = parts
      } else if (mime.startsWith('text/') && mime !== 'text/csv') {
        body = await response.text()
      } else {
        const bodyBlob = await response.blob()
        const bodyBytes = new Uint8Array(
          await new Response(bodyBlob).arrayBuffer(),
        )
        body = bodyBytes
      }
      return body
    })()
    documentBodyPromiseCache.set(cacheKey, fetchPromise)
    fetchPromise.catch(() => {
      documentBodyPromiseCache.delete(cacheKey)
    })
    return fetchPromise
  }, [getDownloadURL, mime, contentId])
}

export const useExclusions = (
  evidenceId: string,
  shouldFetch = true,
): GrpcResponse<GetExclusionsResponse> => {
  const request = new GetExclusionsRequest().setEvidenceid(
    new StringValue().setValue(evidenceId),
  )
  const { response } = useSwrImmutableGrpc(
    AttachmentPromiseClient.prototype.getExclusions,
    request,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useTestTableCsv = (
  contentId: string,
  shouldFetch = true,
  headerTooltip?: ColumnHeaderTooltip,
): {
  rows: GridValidRowModel[]
  columns: GridColDef[]
  isLoading: boolean
  error: string | null
} => {
  const [rows, setRows] = useState<GridValidRowModel[]>([])
  const [columns, setColumns] = useState<GridColDef[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const getDownloadURL = useGetDownloadUrl(contentId)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const url = await getDownloadURL()
        if (!url) {
          throw new Error(ToastPrompts.DOC_DOWNLOAD_ERROR)
        }
        const headers = url.getHeadersMap().getEntryList()

        const response: Response = await fetch(url.getUrl(), {
          method: HTTP_METHODS.GET,
          headers: new Headers(headers as unknown as HeadersInit),
        })
        if (!response || response.status !== 200) {
          throw new Error(ToastPrompts.DOC_DOWNLOAD_ERROR)
        }
        const csvString = await response.text()
        const { rows, columns } = getEvidenceCsv(csvString, headerTooltip)
        setRows(rows)
        setColumns(columns)
      } catch (err) {
        setError(
          isGrpcError(err) || err instanceof Error
            ? err.message
            : 'An error occurred while fetching the test table',
        )
      } finally {
        setIsLoading(false)
      }
    }
    shouldFetch && fetchData()
  }, [getDownloadURL, contentId, shouldFetch, headerTooltip])

  return {
    rows,
    columns,
    isLoading,
    error,
  }
}

export const useEvidenceExcel = (
  contentId: string,
  shouldFetch = true,
  headerTooltip?: ColumnHeaderTooltip,
): {
  rows: GridValidRowModel[]
  columns: GridColDef[]
  isLoading: boolean
  error: string | null
} => {
  const [rows, setRows] = useState<GridValidRowModel[]>([])
  const [columns, setColumns] = useState<GridColDef[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const getDownloadURL = useGetDownloadUrl(contentId)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const url = await getDownloadURL()

        if (!url) {
          throw new Error(ToastPrompts.DOC_DOWNLOAD_ERROR)
        }
        const headers = url.getHeadersMap().getEntryList()

        const response: Response = await fetch(url.getUrl(), {
          method: HTTP_METHODS.GET,
          headers: new Headers(headers as unknown as HeadersInit),
        })

        if (!response || response.status !== 200) {
          throw new Error(ToastPrompts.DOC_DOWNLOAD_ERROR)
        }

        const data = await response.arrayBuffer()
        const bytes = new Uint8Array(data)
        const sheet = getExcelSheet(bytes)

        if (sheet.length === 0) return

        const newColumns = getExcelColumns(sheet)
        const newRows = getExcelRows(sheet, newColumns)

        setColumns(newColumns)
        setRows(newRows)
      } catch (err) {
        setError(
          isGrpcError(err) || err instanceof Error
            ? err.message
            : 'An error occurred while fetching the test table',
        )
      } finally {
        setIsLoading(false)
      }
    }
    shouldFetch && fetchData()
  }, [getDownloadURL, contentId, shouldFetch, headerTooltip])

  return {
    rows,
    columns,
    isLoading,
    error,
  }
}

export type DownloadUrlProps = {
  url: ProtoURL
  mime: string
  fileName: string
}

export const useDownloadUrl = (): ((
  input: DownloadUrlProps,
) => Promise<void>) => {
  const getUrlBodies = useFetchUrlBodies()
  return useCallback(
    async ({ url, mime, fileName }: DownloadUrlProps) => {
      const bodies = await getUrlBodies([url])
      const body = bodies[0]
      if (!body) {
        log.error('Document body not found')
        return
      }
      const file = URL.createObjectURL(
        new Blob([body as BlobPart], { type: mime }),
      )
      FileSaver.saveAs(file, fileName)
    },
    [getUrlBodies],
  )
}
